import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Search, ChevronDown, Smartphone, MessageSquare } from "lucide-react";
import { useCart } from "../context/CartContext";

const PRODUCTS = [
  {
    to: "/phones",
    label: "Phantom Phone",
    tagline: "Encrypted Google Pixels on hardened GrapheneOS.",
    icon: Smartphone,
    logo: "/img/phantom-phone-logo.png",
  },
  {
    to: "/chat",
    label: "Phantom X",
    tagline: "Anonymous encrypted messenger. USB-key gated.",
    icon: MessageSquare,
    logo: "/img/phantom-key-badge.png",
  },
];

const LINKS = [
  { to: "/tiers", label: "Tiers" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const productsRef = useRef(null);
  const { count } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setProductsOpen(false);
  }, [location.pathname]);

  // Close products dropdown on outside click
  useEffect(() => {
    if (!productsOpen) return;
    const onClickOutside = (e) => {
      if (productsRef.current && !productsRef.current.contains(e.target)) {
        setProductsOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [productsOpen]);

  const productsActive = PRODUCTS.some((p) => location.pathname.startsWith(p.to));

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/5"
          : "backdrop-blur-md bg-black/40 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 h-14">
        <Link to="/" className="flex items-center group" data-testid="nav-brand">
          <img
            src="/img/wordmark.png?v=2"
            alt="Phantom Industries"
            className="h-10 w-auto object-contain select-none group-hover:opacity-90 transition-opacity"
            draggable="false"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            className={`px-4 py-2 text-[13px] font-medium transition-colors ${
              location.pathname === "/" ? "text-white" : "text-white/60 hover:text-white"
            }`}
          >
            Home
          </Link>

          {/* Products dropdown */}
          <div className="relative" ref={productsRef}>
            <button
              type="button"
              onClick={() => setProductsOpen((v) => !v)}
              className={`inline-flex items-center gap-1 px-4 py-2 text-[13px] font-medium transition-colors ${
                productsActive || productsOpen ? "text-white" : "text-white/60 hover:text-white"
              }`}
              data-testid="nav-products-btn"
              aria-expanded={productsOpen}
            >
              Products
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${productsOpen ? "rotate-180" : ""}`} />
            </button>

            {productsOpen && (
              <div
                className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[420px] rounded-2xl border border-white/10 bg-black/95 backdrop-blur-xl shadow-2xl shadow-purple-500/10 overflow-hidden"
                data-testid="nav-products-menu"
              >
                <div className="p-2">
                  {PRODUCTS.map((p) => {
                    const Icon = p.icon;
                    return (
                      <Link
                        key={p.to}
                        to={p.to}
                        className="flex items-start gap-3 p-3 rounded-xl hover:bg-purple-500/10 transition-colors group"
                        data-testid={`nav-products-${p.to.slice(1)}`}
                        onClick={() => setProductsOpen(false)}
                      >
                        <div
                          className="h-11 w-11 rounded-xl bg-purple-500/5 border border-purple-500/20 bg-cover bg-center flex-shrink-0 flex items-center justify-center"
                          style={{ backgroundImage: `url(${p.logo})` }}
                        >
                          {/* Fallback icon shown if bg image not present */}
                          <Icon className="h-4 w-4 text-purple-300/0" aria-hidden />
                        </div>
                        <div className="min-w-0">
                          <div className="font-display text-sm font-semibold text-white group-hover:text-purple-200 transition-colors flex items-center gap-2">
                            <Icon className="h-3.5 w-3.5 text-purple-300" />
                            {p.label}
                          </div>
                          <div className="text-xs text-white/50 mt-0.5 leading-snug">
                            {p.tagline}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
                <div className="px-4 py-3 border-t border-white/5 bg-white/[0.02] text-[10px] uppercase tracking-[0.2em] text-white/40">
                  A family of privacy tools · from Phantom Industries
                </div>
              </div>
            )}
          </div>

          {LINKS.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                className={`px-4 py-2 text-[13px] font-medium transition-colors ${
                  active ? "text-white" : "text-white/60 hover:text-white"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <button
            aria-label="Search"
            className="hidden md:inline-flex h-9 w-9 items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/5 transition-colors"
          >
            <Search className="h-4 w-4" />
          </button>
          <Link
            to="/cart"
            aria-label="Cart"
            className="relative inline-flex h-9 w-9 items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/5 transition-colors"
          >
            <ShoppingBag className="h-4 w-4" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-purple-500 text-[10px] font-bold text-black flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>
          <button
            aria-label="Menu"
            className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/5"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t border-white/5 bg-black/95 backdrop-blur-xl">
          <nav className="flex flex-col px-4 py-3">
            <Link to="/" className="py-3 px-2 text-sm text-white/80 hover:text-white border-b border-white/5">
              Home
            </Link>
            <div className="py-2 px-2 text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">
              Products
            </div>
            {PRODUCTS.map((p) => (
              <Link
                key={p.to}
                to={p.to}
                className="py-3 px-2 text-sm text-white/80 hover:text-white border-b border-white/5"
              >
                {p.label}
              </Link>
            ))}
            <div className="py-2 px-2 text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2">
              Company
            </div>
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="py-3 px-2 text-sm text-white/80 hover:text-white border-b border-white/5 last:border-none"
              >
                {l.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
